/**
 * Internal/system custom-field keys that must never be shown to an agent.
 *
 * These are written by automation, not by people: workflow-service maps webhook
 * responses onto customFields (quote links, broker portals, Horizon status) and
 * on a quote-validation failure it writes a cluster of failure fields including
 * pre-rendered HTML tables intended only for an email template. The chat/AI
 * intake path adds its own bookkeeping keys.
 *
 * Consumed by the ticket sidebar's Contact Details block and by the History
 * timeline (which collapses any run of these into one neutral line rather than
 * dumping raw HTML per field). Kept in sync with the ticket-service copy at
 * centraOnePlus-ticket-service/src/modules/tickets/internal-custom-fields.util.ts.
 */

// Core system fields to always exclude from display (exact match on the raw key).
export const coreSystemFields = new Set([
  'chatSessionId',
  'visitorId',
  'conversationSummary',
  'customerIntent',
  'collectedFields',
  'ticketTypeId',
  'channel',
  'source',
  '_aiMetadata',
  'lineItemDetails',
  // Quote-related fields (should only appear in Line Items)
  'quotationLink',
  'quotation_link',
  'quoteLink',
  'quote_link',
  'brokerLink',
  'broker_link',
  'horizonStatus',
  'horizon_status',
  'horizonError',
  'horizon_error',
  // Automation bookkeeping stamped onto line items by workflow-service when it
  // writes AI-extracted fields or an external-API result.
  'lastUpdatedFromEmail',
  'externalApiStatus',
  'externalApiError',
  // Common API response field names that may contain quote URLs
  'data',
  'link',
  'url',
]);

// Patterns for fields that should be excluded from display.
// NOTE: tested against normalized keys (lowercase, no underscores/spaces).
export const systemFieldPatterns = [
  /^_/, // Fields starting with underscore are internal
  /quota?tionlink/i, // quotationLink, quotation_link, etc.
  /quotelink/i, // quoteLink, quote_link, etc.
  /brokerlink/i, // brokerLink, broker_link, etc.
  /horizon(status|error)/i, // horizonStatus, horizonError, etc.
  /fail(ure|ed)/i, // Any field containing failure/failed
  /quote.*fail/i, // quoteFailureReason, quoteFailedItems, etc.
  /validation.*fail/i, // quoteValidationFailed, etc.
  /itemshtml/i, // itemsHtml, Items Html, etc.
  /itemstext/i, // itemsText, Items Text (when related to failure)
  /workflow/i, // workflowRetryCount, Workflow Retry Count, etc. — internal
  /^externalapi/i, // externalApiStatus, externalApiError, etc.
];

/**
 * Checks if a field key should be hidden from the UI.
 * Uses both exact matches and pattern matching for flexibility.
 */
export function isSystemField(fieldKey: string): boolean {
  // Normalize the key for comparison (lowercase, remove underscores and spaces)
  const normalizedKey = fieldKey.toLowerCase().replace(/[_\s]/g, '');

  // Check core system fields (exact match on original key)
  if (coreSystemFields.has(fieldKey)) {
    return true;
  }

  // Check against patterns using normalized key for consistent matching
  return systemFieldPatterns.some(pattern => pattern.test(normalizedKey));
}
